import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

const POLL_INTERVAL_MS = 2 * 60 * 1000;

const hashString = (value: string): string => {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return String(hash >>> 0);
};

const getVersionSignature = async (): Promise<string | null> => {
  const response = await fetch('/index.html', {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });

  if (!response.ok) {
    return null;
  }

  const etag = response.headers.get('etag');
  if (etag) {
    return `etag:${etag}`;
  }

  const lastModified = response.headers.get('last-modified');
  if (lastModified) {
    return `last-modified:${lastModified}`;
  }

  const html = await response.text();
  return `body-hash:${hashString(html)}`;
};

export default function UpdateAvailableBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const isCheckingRef = useRef(false);
  const signatureRef = useRef<string | null>(null);
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') {
      return;
    }

    let isMounted = true;

    const checkForNewVersion = async () => {
      if (!isMounted || isCheckingRef.current || updateAvailable) {
        return;
      }

      isCheckingRef.current = true;
      try {
        const nextSignature = await getVersionSignature();
        if (!nextSignature || !isMounted) {
          return;
        }

        if (!signatureRef.current) {
          signatureRef.current = nextSignature;
          return;
        }

        if (nextSignature !== signatureRef.current) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        console.error('Version check failed:', error);
      } finally {
        isCheckingRef.current = false;
      }
    };

    void checkForNewVersion();
    const intervalId = window.setInterval(() => {
      void checkForNewVersion();
    }, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [isWeb, updateAvailable]);

  if (!isWeb || !updateAvailable) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#111827',
        color: '#ffffff',
        borderRadius: 10,
        padding: '10px 12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 14,
      }}
      role="status"
      aria-live="polite"
    >
      <span>A new version is available.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          border: 0,
          borderRadius: 8,
          background: '#10b981',
          color: '#0b1320',
          fontWeight: 700,
          cursor: 'pointer',
          padding: '6px 10px',
        }}
      >
        Refresh
      </button>
    </div>
  );
}
